import nookies, { parseCookies, setCookie, destroyCookie } from "nookies";
import { getOwnProfile, getPremiumStatus, getBusinessProfile, exchangeAppHandoffCode } from "./api";
import { decryptString, encryptString } from "./encryption";
import { handleSetDataRedux } from "./general";
import { getInternalProfile } from "./internalRequest";

// Session lifetime: 3 days. Applied to both the `authorization` cookie
// (maxAge) and the `userProfile` localStorage entry (timestamp wrapper).
// Expired sessions are treated as logged-out on next read.
const AUTH_TTL_SECONDS = 3 * 24 * 60 * 60;
const AUTH_TTL_MS = AUTH_TTL_SECONDS * 1000;
const AUTH_COOKIE_NAME = "authorization";

const getHostnameFromCtx = (ctx) => {
  if (ctx && ctx.req && ctx.req.headers && ctx.req.headers.host) {
    return ctx.req.headers.host.split(":")[0];
  }
  if (typeof window !== "undefined" && window.location && window.location.hostname) {
    return window.location.hostname;
  }
  const domain = process.env.FULL_DOMAIN || process.env.DOMAIN || "";
  if (!domain) return "";
  try {
    return new URL(domain).hostname;
  } catch (e) {
    return domain.replace(/^https?:\/\//, "").split("/")[0].split(":")[0];
  }
};

// security-hardening-2026: `httpOnly` is intentionally NOT set here. The
// client reads this cookie's value directly and pervasively (getToken,
// getTokenUser, getAuthorizationHeader, getOwnUserProfile in this file are
// all called with no ctx from browser code to build Authorization headers
// for client-triggered API calls). Making it httpOnly would silently break
// every authenticated request the SPA makes outside of getInitialProps/SSR.
// A real fix needs either a session-token exchange endpoint or proxying all
// client-side authenticated calls through server routes — both are
// app-wide refactors out of scope here; flagged for a dedicated follow-up.
// `secure` is safe to add now (SSR reads via req.headers.cookie are
// unaffected by it) and is applied in production.
const getAuthCookieOptions = (ctx) => {
  const options = {
    maxAge: AUTH_TTL_SECONDS,
    path: "/",
    sameSite: "lax",
    secure: process.env.NODE_ENV === "production",
  };
  const hostname = getHostnameFromCtx(ctx);
  if (hostname && hostname !== "localhost" && hostname.endsWith(".localhost")) {
    options.domain = ".localhost";
  }
  // security-hardening-2026: no `domain` for the myfitsociety.com host —
  // the authorization cookie is now host-only (no Domain attribute), so it
  // is never sent to sibling subdomains. migrateLegacyCookies() still
  // destroys the old .myfitsociety.com-scoped copies on load.
  return options;
};

const normalizeAuthorizationObj = (authorizationObj = {}) => {
  if (authorizationObj && authorizationObj.authorization) {
    return authorizationObj.authorization;
  }
  if (authorizationObj && authorizationObj.data && authorizationObj.data.access_token) {
    return authorizationObj.data;
  }
  // Some auth responses only expose `token` (not access_token).
  if (authorizationObj && !authorizationObj.access_token && authorizationObj.token) {
    return {
      ...authorizationObj,
      access_token: authorizationObj.token,
    };
  }
  return authorizationObj || {};
};

// One-page handoff when Set-Cookie is flaky across the OAuth return navigation
// (auth-redirect-landing → /quiz). sessionStorage is same-origin and survives
// a single full page navigation; destination consumes and clears it.
const AUTH_HANDOFF_KEY = "mfs_auth_handoff_v1";

export const stashAuthHandoff = (authorizationObj = {}) => {
  if (typeof window === "undefined") return false;
  const authorization = normalizeAuthorizationObj(authorizationObj);
  if (!authorization.access_token) return false;
  try {
    sessionStorage.setItem(
      AUTH_HANDOFF_KEY,
      JSON.stringify({
        access_token: authorization.access_token,
        token_type: authorization.token_type || "Bearer",
        email: authorization.email || "",
        ts: Date.now(),
      })
    );
    return true;
  } catch (e) {
    return false;
  }
};

export const consumeAuthHandoff = () => {
  if (typeof window === "undefined") return false;
  try {
    const raw = sessionStorage.getItem(AUTH_HANDOFF_KEY);
    if (!raw) return false;
    sessionStorage.removeItem(AUTH_HANDOFF_KEY);
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.access_token) return false;
    // Ignore stale handoffs older than 10 minutes.
    if (parsed.ts && Date.now() - Number(parsed.ts) > 10 * 60 * 1000) {
      return false;
    }
    return setAuthorizationCookie(null, {
      access_token: parsed.access_token,
      token_type: parsed.token_type || "Bearer",
      email: parsed.email || undefined,
    });
  } catch (e) {
    return false;
  }
};

const writeUserProfileLocal = (userData) => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(
      "userProfile",
      JSON.stringify({ data: userData, ts: Date.now() })
    );
  } catch (e) { /* localStorage full or disabled */ }
};

// Reads userProfile from localStorage, honoring the 3-day TTL.
// Returns null + self-cleans when expired or malformed.
const readUserProfileLocal = () => {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem("userProfile");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && parsed.ts && parsed.data) {
      if (Date.now() - parsed.ts > AUTH_TTL_MS) {
        localStorage.removeItem("userProfile");
        destroyCookie(null, "authorization", { path: "/" });
        return null;
      }
      return parsed.data;
    }
    // Legacy unwrapped entry (pre-TTL). Treat as fresh, re-wrap.
    writeUserProfileLocal(parsed);
    return parsed;
  } catch (e) {
    return null;
  }
};

export const makeid = (length) => {
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const handleCookie = (ctx) => {
  const cookies = parseCookies(ctx);
  const options = {
    maxAge: 60 * 24 * 60 * 60,
    path: "/",
  };
  if (!cookies.cookie_id) {
    setCookie(ctx, "cookie_id", makeid(40), options);
  }
  return cookies;
};

export const handleHeader = (cookie) => {
  if (cookie === "client") {
    const clientCookies = parseCookies();
    return {
      "X-Cookie-Id": clientCookies.cookie_id,
    };
  }
  if (cookie.cookie_id) {
    return {
      "X-Cookie-Id": cookie.cookie_id,
    };
  }
  return {};
};

export const setAuthorizationCookie = (ctx, authorizationObj = {}) => {
  const authorization = normalizeAuthorizationObj(authorizationObj);
  if (!authorization.access_token) {
    return false;
  }
  const siteAuth = {
    access_token: encryptString(authorization.access_token),
  };
  if (authorization.token_type) {
    siteAuth.token_type = encryptString(authorization.token_type);
  }
  setCookie(ctx, AUTH_COOKIE_NAME, JSON.stringify(siteAuth), getAuthCookieOptions(ctx));
  // Also set host-only without domain override as a belt-and-suspenders path for
  // staging hosts (mfsdev.net) where domain cookie edge-cases can drop the session.
  if (typeof window !== "undefined") {
    try {
      const hostOnly = {
        maxAge: AUTH_TTL_SECONDS,
        path: "/",
        sameSite: "lax",
        secure: process.env.NODE_ENV === "production",
      };
      setCookie(null, AUTH_COOKIE_NAME, JSON.stringify(siteAuth), hostOnly);
    } catch (e) { /* ignore */ }
  }
  const userData = { ...authorization };
  userData.access_token = "";
  userData.refresh_token = "";
  userData.token_type = "";
  writeUserProfileLocal(userData);
  return true;
};
export const setAppToken = (ctx, access_token) => {
  const siteAuth = {
    access_token: encryptString(access_token)
  };
  setCookie(ctx, AUTH_COOKIE_NAME, JSON.stringify(siteAuth), getAuthCookieOptions(ctx));
};

/**
 * RN appends `wv=true` on every collab landing URL (Trello 222 / 12 AUG + 20 AUG).
 * After applyAppTokenQuery strips `app_token`, `wv` stays in the query.
 */
export const isFromAppQuery = (query) => {
  if (!query) return false;
  const raw = query.wv;
  const wv = Array.isArray(raw) ? raw[0] : raw;
  return wv === true || wv === "true" || wv === 1 || wv === "1";
};

/**
 * App → web handoff for /collab/get-plan and /collab/package-list.
 * Leftover web session must never beat the app token (Trello 222 / 18 AUG 26).
 * Always destroy authorization cookies, write the app JWT, then 302 self-redirect
 * with `app_token` stripped so this request cannot profile the previous user.
 *
 * @returns {boolean} true when this request already issued a self-redirect
 */
const stripAppTokenFromAsPath = (asPath) => {
  if (!asPath) return "/";
  try {
    const url = new URL(asPath, "https://placeholder.local");
    url.searchParams.delete("app_token");
    url.searchParams.delete("hc");
    return `${url.pathname}${url.search}`;
  } catch (error) {
    return asPath;
  }
};

const firstQueryValue = (value) => (Array.isArray(value) ? value[0] : value);

/**
 * Trello 437 V5: `?hc=<code>` is the handoff shape that does NOT leak.
 *
 * The app used to pass its JWT as `?app_token=`, which lands in browser history,
 * nginx and CDN logs, and any Referer the page sends onward. A code is single
 * use, expires in five minutes, and is redeemed here — server side — for the
 * session, so the token itself never reaches the address bar.
 *
 * `app_token` still works on purpose: builds already installed on phones keep
 * sending it, and they must keep working.
 *
 * @returns {Promise<boolean>} true when this request issued a self-redirect
 */
export const applyAppTokenQuery = async (ctx) => {
  if (!ctx || !ctx.query) return false;
  const appToken = firstQueryValue(ctx.query.app_token);
  const handoffCode = firstQueryValue(ctx.query.hc);
  if (!appToken && !handoffCode) return false;

  let token = appToken;
  if (!token && handoffCode) {
    try {
      const response = await exchangeAppHandoffCode(handoffCode);
      token = (response && response.data && response.data.access_token) || null;
    } catch (error) {
      token = null;
    }
    // A spent, expired or forged code simply means "no app session": render the
    // page as an anonymous visitor rather than failing it.
    if (!token) return false;
  }

  destroyUserCookie(ctx);
  setAppToken(ctx, token);
  if (!ctx.res || ctx.res.headersSent || ctx.res.finished) return false;
  if (typeof ctx.res.setHeader !== "function") return false;

  ctx.res.statusCode = 302;
  ctx.res.setHeader("Location", stripAppTokenFromAsPath(ctx.asPath || "/"));
  ctx.res.end();
  return true;
};

export const setUserData = (authorizationObj) => {
  const userData = {
    ...authorizationObj,
  };
  userData.access_token = "";
  userData.refresh_token = "";
  userData.token_type = "";
  writeUserProfileLocal(userData);
};

// One-time cleanup: migrate legacy bloat cookies to localStorage, then destroy.
// Previously `userData` and `siteSetting` were stored as JSON cookies, which
// accumulated past Node's header limit and caused Cloudflare 520s.
export const migrateLegacyCookies = () => {
  if (typeof window === "undefined") return;
  const cookies = parseCookies(null);
  const migrations = [
    { name: "userData", lsKey: "userProfile" },
    { name: "siteSetting", lsKey: "siteSetting" },
  ];
  migrations.forEach(({ name, lsKey }) => {
    if (!cookies[name]) return;
    try {
      if (!localStorage.getItem(lsKey)) {
        if (name === "userData") {
          try {
            writeUserProfileLocal(JSON.parse(cookies[name]));
          } catch (e) { /* malformed legacy cookie, skip migration */ }
        } else {
          localStorage.setItem(lsKey, cookies[name]);
        }
      }
    } catch (e) { /* ignore */ }
    destroyCookie(null, name, { path: "/" });
    destroyCookie(null, name, { path: "/", domain: ".mfsdev.net" });
    destroyCookie(null, name, { path: "/", domain: ".myfitsociety.com" });
  });
  ["businessProfile", "businessAuthorization"].forEach((name) => {
    if (!cookies[name]) return;
    destroyCookie(null, name, { path: "/business" });
    destroyCookie(null, name, { path: "/id/business" });
  });
};

export const setPageCookie = (ctx, value = "") => {
  const options = {
    maxAge: 200 * 24 * 60 * 60,
    path: "/",
  };
  setCookie(ctx, "pageStatus", value, options);
};

export const setBuyPageCookie = (ctx, value = "") => {
  const MAX_BUY_PAGE_LENGTH = 512;
  let safeValue = String(value || "");
  if (safeValue.length > MAX_BUY_PAGE_LENGTH) {
    try {
      const url = new URL(safeValue);
      // Trello 481 (18 SEP 26): an over-cap URL previously lost its ENTIRE
      // query, so the failed-payment "Try again" bounce returned to checkout
      // without page_source/promo_* and the summary re-priced to the default
      // instead of the landing-page price. The display_* params are only
      // first-paint cosmetic hints — the authoritative price re-resolves from
      // page_source/promo_* on the server — so shed them first; the bare
      // origin+pathname fallback remains for genuinely oversized URLs.
      const params = new URLSearchParams(url.search);
      const essentialParams = new URLSearchParams();
      params.forEach((paramValue, paramKey) => {
        if (paramKey.indexOf("display_") !== 0) {
          essentialParams.set(paramKey, paramValue);
        }
      });
      const essentialQuery = essentialParams.toString();
      safeValue = `${url.origin}${url.pathname}${essentialQuery ? `?${essentialQuery}` : ""}`;
      if (safeValue.length > MAX_BUY_PAGE_LENGTH) {
        safeValue = `${url.origin}${url.pathname}`;
      }
    } catch (e) {
      safeValue = safeValue.slice(0, MAX_BUY_PAGE_LENGTH);
    }
    if (safeValue.length > MAX_BUY_PAGE_LENGTH) {
      safeValue = safeValue.slice(0, MAX_BUY_PAGE_LENGTH);
    }
  }
  const options = {
    maxAge: 200 * 24 * 60 * 60,
    path: "/",
  };
  setCookie(ctx, "buyPage", safeValue, options);
};

export const destroyUserCookie = (ctx) => {
  destroyCookie(ctx || null, AUTH_COOKIE_NAME, { path: "/" });
  destroyCookie(ctx || null, AUTH_COOKIE_NAME, { path: "/", domain: ".localhost" });
  destroyCookie(ctx || null, AUTH_COOKIE_NAME, { path: "/", domain: ".myfitsociety.com" });
  if (parseCookies(ctx || {}).userData) {
    destroyCookie(ctx || null, "userData", { path: "/" });
  }
  // Clear Cleeng SDK browser state so a subsequent login doesn't inherit
  // the prior customer's session ("good news, your account already gives you
  // access..." bug, Trello WEB card item 14).
  if (typeof window !== "undefined") {
    try {
      // Lazy require to avoid SSR `window` access.
      // eslint-disable-next-line global-require
      const { clearCleengSessionCache } = require("./cleengSession");
      clearCleengSessionCache();
    } catch (error) {
      // Ignore — safe no-op if helper missing in older bundles.
    }
  }
};

export const destroyPageStatus = () => {
  destroyCookie(null, "pageStatus", { path: "/" });
};

export const deleteFbclidCookie = (ctx) => {
  destroyCookie(ctx || null, "fbclid", { path: "/" });
};

const readLegacyUserDataCookie = (ctx) => {
  const userDataCookie = parseCookies(ctx).userData;
  if (!userDataCookie) {
    return null;
  }
  try {
    return JSON.parse(userDataCookie);
  } catch (e) {
    return null;
  }
};

export const getOwnUserProfile = async (ctx = {}) => {
  try {
    const authCookie = parseCookies(ctx).authorization;
    if (authCookie) {
      const headers = {
        authorization: JSON.parse(authCookie).access_token,
      };
      const profileCookie = await getInternalProfile(headers, ctx);
      if (profileCookie) {
        return profileCookie.data;
      }
    }
    return readUserProfileLocal() || readLegacyUserDataCookie(ctx);
  } catch (err) {
    return readUserProfileLocal() || readLegacyUserDataCookie(ctx);
  }
};

export const getPageStatus = async (ctx = {}) => {
  // eslint-disable-next-line prefer-destructuring
  const pageStatus = parseCookies(ctx).pageStatus;
  if (pageStatus) {
    return pageStatus;
  }
  return null;
};

export const getBuyPage = async (ctx = {}) => {
  const pageStatus = parseCookies(ctx).buyPage;
  if (pageStatus) {
    return pageStatus;
  }
  return null;
};

export const getUserPremiumStatus = async (ctx = {}, params) => {
  const authCookie = parseCookies(ctx).authorization;
  if (authCookie) {
    const currentToken = JSON.parse(authCookie).access_token;
    const profileCookie = await getPremiumStatus(
      decryptString(currentToken),
      params,
      ctx
    );
    if (profileCookie) {
      const final = profileCookie.data;
      return final;
    }
  }
  return null;
};

export const getToken = async (ctx = {}) => {
  const authCookie = parseCookies(ctx).authorization;
  if (authCookie) {
    const currentToken = JSON.parse(authCookie);
    if (currentToken.email || currentToken.id) {
      destroyCookie(ctx || null, "authorization", { path: "/" });
      return null;
    }
    if (currentToken.access_token) {
      return decryptString(currentToken.access_token);
    }
  }
  return null;
};

export const getAuthorizationHeader = (ctx = null) => {
  let authCookie = null;
  if (ctx) {
    authCookie = parseCookies(ctx).authorization;
  } else {
    authCookie = parseCookies({}).authorization;
  }
  if (authCookie) {
    const final = JSON.parse(authCookie);
    return {
      Authorization: `Bearer ${decryptString(final.access_token)}`,
    };
  }
  return {};
};

export const getBusinessAuthorizationHeader = (ctx) => {
  let authCookie = null;
  if (ctx) {
    authCookie = parseCookies(ctx).businessAuthorization;
  } else {
    authCookie = parseCookies({}).businessAuthorization;
  }
  if (authCookie) {
    const final = JSON.parse(authCookie);
    return {
      Authorization: `Bearer ${final.access_token}`,
    };
  }
  return {};
};

export const setAuthorizationBusinessCookie = (ctx, authorizationObj) => {
  const options = {
    maxAge: 200 * 24 * 60 * 60,
    path: "/business",
    sameSite: "lax",
    secure: process.env.NODE_ENV === "production",
  };
  const options2 = {
    maxAge: 200 * 24 * 60 * 60,
    path: "/id/business",
    sameSite: "lax",
    secure: process.env.NODE_ENV === "production",
  };
  setCookie(
    ctx,
    "businessAuthorization",
    JSON.stringify(authorizationObj),
    options
  );
  setCookie(
    ctx,
    "businessAuthorization",
    JSON.stringify(authorizationObj),
    options2
  );
};

export const getBusinessEmailAccount = (ctx = {}) => {
  const emailCookie = parseCookies(ctx).businessAuthorization;
  if (emailCookie) {
    return emailCookie;
  }
  return {};
};

export const getBusinessAuthorizationProfile = async (ctx = {}) => {
  const authCookie = parseCookies(ctx).businessAuthorization;
  if (authCookie) {
    const currentToken = JSON.parse(authCookie).access_token;
    const profileCookie = await getBusinessProfile({}, currentToken);
    if (profileCookie) {
      const final = profileCookie.data;
      return final;
    }
  }
  return {};
};

export const setBusinessProfileCookie = (ctx, profileData) => {
  const options = {
    maxAge: 200 * 24 * 60 * 60,
    path: "/business",
  };
  const options2 = {
    maxAge: 200 * 24 * 60 * 60,
    path: "/id/business",
  };
  setCookie(ctx, "businessProfile", JSON.stringify(profileData), options);
  setCookie(ctx, "businessProfile", JSON.stringify(profileData), options2);
};

export const destroyBusinessProfileCookie = (ctx = null) => {
  if (ctx) {
    destroyCookie(ctx, "businessProfile");
    destroyCookie(ctx, "businessAuthorization");
  } else {
    destroyCookie({}, "businessProfile");
    destroyCookie({}, "businessAuthorization");
  }
};

export const getTokenUser = (ctx = null) => {
  let authCookie = null;
  if (ctx) {
    authCookie = parseCookies(ctx).authorization;
  } else {
    authCookie = parseCookies({}).authorization;
  }
  if (authCookie) {
    const final = JSON.parse(authCookie);
    if (final.email || final.id) {
      destroyCookie(ctx || null, "authorization", { path: "/" });
      destroyCookie(ctx || null, "userData", { path: "/" });
      return null;
    }
    return final.access_token;
  }
  return "";
};

export const getCookieQuestionnaire = (ctx) => {
  let questionnaireCookie = null;
  if (ctx) {
    questionnaireCookie = nookies.get(ctx).squestionnaire;
  } else {
    questionnaireCookie = parseCookies({}).squestionnaire;
  }
  if (questionnaireCookie) {
    return questionnaireCookie;
  }
  return null;
};

export const getCookieReference = (ctx) => {
  let questionnaireCookie = null;
  if (ctx) {
    questionnaireCookie = nookies.get(ctx).reference;
  } else {
    questionnaireCookie = parseCookies({}).reference;
  }
  if (questionnaireCookie) {
    return questionnaireCookie;
  }
  return null;
};

// Trello SALES TRACKER (626): 30-day referral code set by setInitialCookie().
export const getCookieReferralCode = (ctx) => {
  const referralCode = ctx ? nookies.get(ctx).mfs_r : parseCookies({}).mfs_r;
  return referralCode || null;
};

export const getCookieGender = (ctx) => {
  let questionnaireCookie = null;
  if (ctx) {
    questionnaireCookie = nookies.get(ctx).gender;
  } else {
    questionnaireCookie = parseCookies({}).gender;
  }
  if (questionnaireCookie) {
    return questionnaireCookie;
  }
  return null;
};

export const getCookieCoupon = (ctx) => {
  let questionnaireCookie = null;
  if (ctx) {
    questionnaireCookie = nookies.get(ctx).coupon;
  } else {
    questionnaireCookie = parseCookies({}).coupon;
  }
  if (questionnaireCookie) {
    return questionnaireCookie;
  }
  return null;
};
